/* eslint-disable no-magic-numbers */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import get from 'lodash/get';

import { UILoader } from '@futrli/ui-components';
import { Error, ERROR_STATUS } from '@futrli/errors';
import { fetchConsolidation } from '@futrli/organisations';
import { matchReportingInternalRenderReportUrl } from '@futrli/shared-services';
import { LiveReportDetail, LiveReportRequest, ReportTemplateDetail, ReportTemplateRequest } from '@futrli-sdk/predict';

import { fetchLiveReportById } from '../../core/api/reporting/report-builder';
import { ReportRenderForPrint } from '../components/report-render-for-print/report-render-for-print';

import { ReportBuilderContextProvider } from '../report-builder.context';
// import { fetchOrganisationById } from '../../core/api/workspace-coordinator';
import { fetchOrganisationById } from '../../core/api/organisations';
import { OnboardingContextProvider } from '../onboarding.context';
import { PredictionsInfoContext } from '../../forecast/detail/context/predictions-info-context';
import { pageSizesOptions } from '../constants';
import { ConsolidationDataContext } from '../../predict-management/context/consolidation-data-context';
import { ConsolidatedForecastingContext } from '../../forecast/detail/context/consolidated-forecasting-context';
import { SyncContextProvider } from '@futrli/predict/core/sync-context/sync-context';

export const RenderReportPage = () => {
  const [report, setReport] = useState<LiveReportDetail | ReportTemplateDetail>();
  const [currency, setCurrency] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const { pathname } = useLocation();
  const isConsolidation = matchReportingInternalRenderReportUrl(pathname, true);

  const workspaceId = pathname.split('/')[3];
  const organisationId = pathname.split('/')[5];
  const reportId = pathname.split('/')[7];

  const patchReport = (_reportId: string, _data: Partial<LiveReportRequest | ReportTemplateRequest>): Promise<void> => {
    return new Promise(resolve => resolve());
  };

  useEffect(() => {
    const getAllLiveReports = async () => {
      try {
        setIsLoading(true);
        const [fetchedReport, organisation, consolidation] = await Promise.all([
          fetchLiveReportById(workspaceId, organisationId, reportId, isConsolidation),
          isConsolidation ? undefined : fetchOrganisationById(workspaceId, organisationId),
          isConsolidation ? fetchConsolidation(workspaceId, organisationId) : undefined
        ]);

        const pageSetup = get(fetchedReport, 'data.pageSetup');
        const pageSize = get(pageSetup, 'pageSize') || pageSizesOptions[0].value;
        const orientation = fetchedReport.orientation || 'portrait';
        window.pageOrientation = orientation;
        window.pageSize = pageSize;

        setReport(fetchedReport);

        if (isConsolidation) {
          consolidation && setCurrency(consolidation.display_currency);
        } else {
          organisation && setCurrency(organisation.datasource.currency_code);
        }
      } catch (e) {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    getAllLiveReports();
  }, [reportId, workspaceId, organisationId, isConsolidation]);

  useEffect(() => {
    document.title = `Futrli - ${report?.name || 'Untitled report'}`;
  }, [report]);

  if (!reportId || !reportId.length) {
    return <Error errorStatus={ERROR_STATUS._404} />;
  }

  if (error) {
    return <Error />;
  }

  if (isLoading) {
    return <UILoader />;
  }

  if (!report) {
    return null;
  }

  return isConsolidation ? (
    <SyncContextProvider>
      <ConsolidationDataContext consolidationId={organisationId} workspaceId={workspaceId}>
        <ConsolidatedForecastingContext>
          <PredictionsInfoContext>
            <OnboardingContextProvider>
              <ReportBuilderContextProvider
                report={report}
                patchReport={patchReport}
                workspaceId={workspaceId}
                organisationId={organisationId}
              >
                <ReportRenderForPrint currency={currency} />
              </ReportBuilderContextProvider>
            </OnboardingContextProvider>
          </PredictionsInfoContext>
        </ConsolidatedForecastingContext>
      </ConsolidationDataContext>
    </SyncContextProvider>
  ) : (
    <SyncContextProvider>
      <PredictionsInfoContext>
        <OnboardingContextProvider>
          <ReportBuilderContextProvider
            report={report}
            patchReport={patchReport}
            workspaceId={workspaceId}
            organisationId={organisationId}
          >
            <ReportRenderForPrint currency={currency} />
          </ReportBuilderContextProvider>
        </OnboardingContextProvider>
      </PredictionsInfoContext>
    </SyncContextProvider>
  );
};
